import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { SectionHeading } from "../components/PageHero";

const About = () => {
  return (
    <PageLayout>
      {/* Our Mission */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24">
          <SectionHeading eyebrow="Our Mission" title="Education Ordered Toward the Highest Things." />
          <div className="space-y-6 font-serif-body text-[17px] leading-[1.85] text-[#3a466e] max-w-[880px] mx-auto">
            <p>The intellect should be trained to recognize truth; the will formed to love what is right and good; the imagination nourished by what is beautiful. Students should acquire the habits necessary to reason well, work diligently, exercise sound judgment, and live virtuously.</p>
            <p>Angel of Peace places the intellectual depth of Catholic education within a balanced rhythm of prayer, study, beauty, friendship, meaningful work, and rest.</p>
            <p>We seek to assist parents in both the education of their children and the formation of their faith — working together toward a shared understanding of what education is for.</p>
            <p className="italic text-[#1e2952]">
              For a deeper dive into the philosophy behind our curriculum, read this article:{" "}
              <Link
                to="/blog/what-is-education-for"
                data-testid="about-blog-link-what-is-education-for"
                className="text-[#c9a961] hover:text-[#1e2952] underline decoration-[#c9a961]/60 underline-offset-2 transition-colors"
              >
                What is the Purpose of Education?
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Why Angel of Peace + Fatima */}
      <section className="bg-[#f6efd8]/40 border-y border-[#eae1c4]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24">
          <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">Living out the message of Fatima</div>
          <h2 className="font-display text-[#1e2952] text-[30px] md:text-[40px] leading-tight" style={{ fontWeight: 500 }}>Named for the Angel of Fatima.</h2>
          <div className="mt-5 h-px w-24 bg-[#c9a961]"></div>
          <div className="mt-6 space-y-5 font-serif-body text-[17px] leading-[1.85] text-[#3a466e]">
            <p>Angel of Peace Academy takes its name from the Angel who appeared to Lucia, Francisco, and Jacinta before the apparitions of Our Lady at Fatima.</p>
            <p>The Angel prepared the children by teaching them to pray, adore, make sacrifice, and offer reparation for sinners — directing their hearts most especially toward the Holy Eucharist.</p>
            <p>
              Through the Little Office of the Blessed Virgin Mary, the Rosary, the Holy Sacrifice of the Mass, and{" "}
              <Link
                to="/electives#orders"
                data-testid="about-link-orders"
                className="text-[#c9a961] hover:text-[#1e2952] underline decoration-[#c9a961]/60 underline-offset-2 transition-colors"
              >
                The Orders
              </Link>
              , students learn to <em className="font-display italic text-[#1e2952]">Live the Faith</em> so they can <em className="font-display italic text-[#1e2952]">Love the Faith</em>.
            </p>
          </div>

          {/* Angel of Peace Academy seal — placed below the Fatima section */}
          <div className="mt-14 md:mt-16 flex justify-center">
            <img src="/assets/seal.png" alt="Angel of Peace Academy Seal" className="w-full max-w-[300px] h-auto object-contain" />
          </div>
        </div>
      </section>

      {/* Our Hope for Our Students */}
      <section className="bg-[#1e2952] text-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24 text-center">
          <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">Our Hope for Our Students</div>
          <h2 className="font-display text-[32px] md:text-[44px]" style={{ fontWeight: 500 }}>Prepared to Live Well.</h2>
          <div className="mt-5 flex items-center justify-center gap-3">
            <span className="block h-px w-40 bg-[#c9a961]"></span>
            <span className="text-[#c9a961]">†</span>
            <span className="block h-px w-40 bg-[#c9a961]"></span>
          </div>
          <div className="mt-10 space-y-6 font-serif-body text-[17px] md:text-[18px] leading-[1.85] text-[#e8e2cf]">
            <p>Young men and women who know and love the Catholic faith, read deeply and think clearly, speak and write well, work diligently, pray faithfully, and embrace God's will with courage and generosity.</p>
            <p className="font-display italic text-[#c9a961] text-[22px] md:text-[26px] leading-[1.55] pt-2" style={{ fontWeight: 500 }}>
              An education that prepares them to live well — and ultimately to become saints.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#fbf7ee] py-16">
        <div className="max-w-[1000px] mx-auto px-6 flex justify-center">
          <Link to="/school-life" className="btn-navy font-sans-nav text-[12px] tracking-[0.24em] uppercase px-8 py-4">
            Explore School Life
          </Link>
        </div>
      </section>

      {/* Inquire CTA */}
      <section className="bg-[#1e2952] text-[#fbf7ee]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 py-20 text-center">
          <h2 className="font-display text-[28px] md:text-[38px] leading-tight" style={{ fontWeight: 500 }}>
            Discover Whether Angel of Peace Is Right for Your Family.
          </h2>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="block h-px w-40 bg-[#c9a961]"></span>
            <span className="text-[#c9a961]">†</span>
            <span className="block h-px w-40 bg-[#c9a961]"></span>
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to="/inquire"
              data-testid="about-cta-inquire"
              className="inline-block font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 bg-[#c9a961] text-[#1e2952] hover:bg-[#e2c98a] transition-colors"
            >
              Inquire Today
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default About;
